'use client';

import { useEffect, useMemo, useState } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { useDialogContext } from '@ravago/shared/page-elements-radiance/hooks/use-dialog-context';
import { useDocumentShare } from '@ravago/shared/page-elements-radiance/hooks/use-document-share';
import { FabAction } from '@ravago/shared/radiance/components/fab/components/FabAction/FabAction';
import { FabMenu } from '@ravago/shared/radiance/components/fab/components/FabMenu/FabMenu';
import { useTranslations } from '@ravago/shared/translations/client';

import { addDays } from 'date-fns';

import { RoleBasedToggles } from '../../../models/config/feature.model';

interface Props {
  feedbackUrl: string | undefined;
  roleBasedToggles: RoleBasedToggles;
}

const Widget = ({ feedbackUrl, roleBasedToggles }: Props) => {
  const translate = useTranslations();
  const { dispatch } = useDialogContext();

  const { user } = useAuthentication();

  const { shareDocumentList, setDownloadList } = useDocumentShare();

  const showFeedback = feedbackUrl && roleBasedToggles.hasTeamdeskFeedbackFeatureToggle;
  const showSharePage = roleBasedToggles.hasShareFeatureToggle;

  const [teamdeskFeedbackUrl, setTeamdeskFeedbackUrl] = useState(feedbackUrl);
  const [menuOpen, setMenuOpen] = useState(false);
  const [widgetLabel, setWidgetLabel] = useState(translate('page-consumer.seller-widget.tools'));
  const [widgetBadge, setWidgetBadge] = useState('');
  const [widgetIcon, setWidgetIcon] = useState('tools');

  const getLocalStorageWarningRemover = () => {
    const previousTimeStampString = localStorage.getItem('hideInteralDocumentWarning');
    const currentTimeStamp = new Date();
    if (!previousTimeStampString) return false;
    const maxTimeStamp = addDays(new Date(previousTimeStampString), 15); // this number indicates 15 days, after that the warning will be shown again
    return currentTimeStamp < maxTimeStamp;
  };

  const shareSubText = useMemo(() => {
    if (shareDocumentList.documents.some((doc) => doc.isInternal)) {
      return translate('page-consumer.seller-widget.share-internal-subtext');
    }
    return undefined;
  }, [shareDocumentList.documents, translate]);

  const shareBadge = useMemo(() => shareDocumentList.documents.filter((doc) => !doc.isInternal).length.toString(), [shareDocumentList.documents]);

  useEffect(() => {
    if (feedbackUrl && user?.mail) {
      setTeamdeskFeedbackUrl(feedbackUrl + user.mail);
    }
  }, [feedbackUrl, user]);

  useEffect(() => {
    if (shareDocumentList.documents.length > 0 && showSharePage) {
      setWidgetBadge(shareBadge);
      setWidgetLabel(translate('page-consumer.seller-widget.share-docs'));
      setWidgetIcon('share-alt');
      return;
    }
    setWidgetBadge('');
    setWidgetLabel(translate('page-consumer.seller-widget.tools'));
    setWidgetIcon('tools');
  }, [shareDocumentList, translate]);

  const onFeedbackClick = () => {
    setMenuOpen(false);
    if (teamdeskFeedbackUrl) window.open(teamdeskFeedbackUrl, '_blank');
  };

  const onSharePageClick = () => {
    setMenuOpen(false);
    if (dispatch) {
      dispatch({ type: 'open_dialog', value: { dialogType: 'share-page', title: 'Share Page' } });
    }
  };

  const onShareDocumentsClick = () => {
    setMenuOpen(false);
    if (dispatch) {
      dispatch({ type: 'open_dialog', value: { dialogType: 'share-documents' } });
    }
  };

  const onDownloadAllClick = () => {
    setMenuOpen(false);
    if (dispatch) {
      if (shareDocumentList.documents.some((doc) => doc.isInternal) && !getLocalStorageWarningRemover())
        dispatch({ type: 'open_dialog', value: { dialogType: 'internal-document-warning', totalAmountOfDocuments: shareDocumentList.documents.length, amountOfInternalDocuments: shareDocumentList.documents.filter((doc) => doc.isInternal).length } });
      else {
        setDownloadList(shareDocumentList.documents);
      }
    }
  };

  const onWidgetClick = () => {
    if (shareDocumentList.documents.length > 0 && widgetIcon === 'share-alt') onShareDocumentsClick();
    else setMenuOpen(true);
  };

  return (
    (showFeedback || showSharePage) && (
      <FabMenu open={menuOpen} onClick={onWidgetClick} onToggle={setMenuOpen} label={widgetLabel} leadingIcon={widgetIcon} badge={widgetBadge} closeOnOutsideClick container={false}>
        {showFeedback && <FabAction label={translate('page-consumer.seller-widget.feedback')} icon="comment-alt" onClick={onFeedbackClick} />}
        {showSharePage && <FabAction label={translate('page-consumer.seller-widget.share-page-text')} icon="share-alt" onClick={onSharePageClick} />}
        {showSharePage && shareDocumentList.documents.length > 0 && <FabAction variant="primary" badge={shareDocumentList.documents.length.toString()} label={translate('page-consumer.seller-widget.download-docs')} icon="download" onClick={onDownloadAllClick} />}
        {showSharePage && shareDocumentList.documents.length > 0 && <FabAction subText={shareSubText} variant="primary" badge={shareBadge} label={translate('page-consumer.seller-widget.share-docs')} icon="share-alt" onClick={onShareDocumentsClick} />}
      </FabMenu>
    )
  );
};

export default Widget;
